import type { Viewport } from "@xyflow/react";

/** Quiet time after the last zoom change before CSS `zoom` comes back. */
export const SHARP_ZOOM_SETTLE_MS = 120;

type SharpZoomSettleOptions = {
  /** Read on every sync; when false, sync applies at once (today's path). */
  isEnabled: () => boolean;
  /** The steady sharp-zoom presentation (CSS `zoom` when it applies). */
  apply: (el: HTMLElement, vp: Viewport) => void;
  /** Drops CSS `zoom`, leaving React Flow's `transform: scale`. */
  suspend: (el: HTMLElement) => void;
  /** Runs right after the settled apply, so native glass re-collects with
   *  the matching rect compensation in the same pass. */
  onSettled: () => void;
  delayMs?: number;
};

/**
 * Sharp zoom re-lays out every frame on each `--rf-zoom` change. While the
 * zoom value is changing, keep the cheap `transform: scale` presentation and
 * re-apply CSS `zoom` once the zoom has been still for `delayMs`. Pans
 * between zoom changes only refresh the pending viewport.
 */
export function createSharpZoomSettle({
  isEnabled,
  apply,
  suspend,
  onSettled,
  delayMs = SHARP_ZOOM_SETTLE_MS,
}: SharpZoomSettleOptions) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastZoom: number | null = null;
  let pending: { el: HTMLElement; vp: Viewport } | null = null;

  const cancel = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    pending = null;
  };

  const settle = () => {
    timer = null;
    const next = pending;
    pending = null;
    if (!next) return;
    apply(next.el, next.vp);
    onSettled();
  };

  const sync = (el: HTMLElement, vp: Viewport) => {
    const zoomChanged = lastZoom !== null && vp.zoom !== lastZoom;
    lastZoom = vp.zoom;
    if (!isEnabled()) {
      cancel();
      apply(el, vp);
      return;
    }
    if (!zoomChanged && timer === null) {
      apply(el, vp);
      return;
    }
    suspend(el);
    pending = { el, vp };
    if (!zoomChanged) return;
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(settle, delayMs);
  };

  return { sync, cancel };
}
